/**
 * VIB-2085: prioritet på feedback — hvor hardt saken rammer den som sender inn.
 *
 * Katalog-mønsteret fra EVENT_TYPES / NOTIFICATION_TYPES: `Record<Key, …>` gjør
 * at tsc krever en oppføring for hvert nivå, så et nytt nivå ikke kan legges
 * til uten label og hjelpetekst.
 *
 * Uavhengig av `type` (bug/feature) med hensikt. En bug kan være kosmetisk, og
 * et forslag kan være det som står mellom noen og en hel arbeidsflyt. Å slå de
 * to sammen ville tvunget avsenderen til å velge mellom «hva er det» og «hvor
 * ille er det», og de spørsmålene har ulike svar.
 *
 * Speiles i `supabase/functions/_shared/slack-feedback.ts` (edge functions deler
 * ikke modulgraf med `src/`, samme grunn som osloDateString er portet dit).
 */

export type FeedbackPriority = "blocking" | "workaround" | "minor";

export interface FeedbackPriorityMeta {
  /** Det avsenderen leser i valget. Skrevet fra deres side, ikke vår. */
  label: string;
  /** Én linje som gjør valget utvetydig. */
  hint: string;
  /** Rekkefølge i UI, mest alvorlig først. */
  order: number;
}

export const FEEDBACK_PRIORITIES: Record<FeedbackPriority, FeedbackPriorityMeta> = {
  blocking: {
    label: "Blokkerer meg",
    hint: "Jeg kommer ikke videre med arbeidet før dette er fikset.",
    order: 0,
  },
  workaround: {
    label: "Feil, men jeg kommer videre",
    hint: "Noe er galt, men jeg har en vei rundt det.",
    order: 1,
  },
  minor: {
    label: "Kosmetisk",
    hint: "Ikke i veien for noe, men verdt å se på.",
    order: 2,
  },
};

/** Nivåene i visningsrekkefølge, mest alvorlig først. */
export const FEEDBACK_PRIORITY_ORDER: FeedbackPriority[] = (
  Object.keys(FEEDBACK_PRIORITIES) as FeedbackPriority[]
).sort((a, b) => FEEDBACK_PRIORITIES[a].order - FEEDBACK_PRIORITIES[b].order);

export function isFeedbackPriority(value: unknown): value is FeedbackPriority {
  return typeof value === "string" && value in FEEDBACK_PRIORITIES;
}

/**
 * Etikett for visning, også for rader som mangler prioritet.
 *
 * NULL betyr «sendt inn før feltet fantes» og vises som ukjent, ALDRI som lav:
 * en gammel blokkerende sak skal ikke se ufarlig ut fordi kolonnen kom etterpå.
 */
export function feedbackPriorityLabel(value: string | null | undefined): string {
  if (!isFeedbackPriority(value)) return "Ukjent prioritet";
  return FEEDBACK_PRIORITIES[value].label;
}

/**
 * Tailwind-klasser for prioritetsbadgen.
 *
 * Bevisst utenfor KPI-paletten i widget-status.ts: dette er ikke et
 * måloppnåelses-tall, det er hvor alvorlig en innmeldt sak er. Grønt ville her
 * betydd «kosmetisk», og det er ikke den grønn-betyr-bra-semantikken
 * KPI-reglene verner om.
 */
export const FEEDBACK_PRIORITY_BADGE: Record<FeedbackPriority, string> = {
  blocking: "bg-red-100 text-red-700",
  workaround: "bg-amber-100 text-amber-700",
  minor: "bg-slate-100 text-slate-600",
};
